/* SPDX-License-Identifier: AGPL-3.0-or-later */

import { Component, OnInit, inject, input } from '@angular/core';
import {
  CommonModule,
  Location,
  LocationStrategy,
  NgOptimizedImage,
  PathLocationStrategy,
} from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { QrCodeModule } from 'ng-qrcode';
import { SharedMediaEndpointService, SharedMediaMetadataDto } from '@api';

@Component({
  selector: 'app-tournament-public-gallery',
  standalone: true,
  imports: [CommonModule, MatCardModule, QrCodeModule, NgOptimizedImage],
  templateUrl: './tournament-public-gallery.component.html',
  styleUrl: './tournament-public-gallery.component.scss',
  animations: [
    trigger('cardAnimation', [
      state(
        'expand',
        style({
          transform: 'scale(1)',
        }),
      ),
      state(
        'shrink',
        style({
          transform: 'scale(0.1)',
        }),
      ),
      transition('expand => shrink', [animate('0.8s')]),
      transition('shrink => expand', [animate('1s')]),
    ]),
  ],
  providers: [Location, { provide: LocationStrategy, useClass: PathLocationStrategy }],
})
export class TournamentPublicGalleryComponent implements OnInit {
  linkToUpload: string = '';
  cards: { image: string; alt: string; author: string; title: string; animationState: string }[] =
    [];
  showQrCode: boolean = false;
  pictures: SharedMediaMetadataDto[] = [];

  tournamentId = input<number>();

  location = inject(Location);
  sharedMediaService = inject(SharedMediaEndpointService);

  noActualImages(): boolean {
    return this.cards.every(c => c.image === 'src');
  }

  ngOnInit() {
    this.sharedMediaService.getPublicSharedMediaByTournament(this.tournamentId()!).subscribe({
      next: (data: SharedMediaMetadataDto[]) => {
        for (let i = 0; i < 8; i++) {
          const card = {
            image: 'src',
            alt: 'Tournament Image',
            author: 'Author',
            title: 'Title',
            animationState: 'expand',
          };
          this.loadImage(data[Math.floor(Math.random() * data.length)], card);
          this.cards.push(card);
          this.setRandomAnimationInterval(card);
        }
        this.pictures = data;
      },
      error: error => {
        console.error('Error fetching pictures:', error);
      },
    });
    this.setupQRCodeInterval();
    this.fetchImages();
    this.linkToUpload = this.location.prepareExternalUrl(
      `#/tournaments/${this.tournamentId()}/upload-image`,
    );
  }

  setupQRCodeInterval() {
    setInterval(() => {
      this.showQrCode = true;
      setTimeout(() => {
        this.showQrCode = false;
        this.fetchImages();
      }, 7000);
    }, 30000);
  }

  setRandomAnimationInterval(card: {
    image: string;
    alt: string;
    author: string;
    title: string;
    animationState: string;
  }) {
    const expandedDuration = 4000 + Math.random() * 10000;
    setTimeout(() => {
      this.toggleAnimation(card, 'shrink');
      this.setRandomAnimationInterval(card);
    }, expandedDuration);
  }

  toggleAnimation(
    card: { image: string; alt: string; author: string; title: string; animationState: string },
    state: string,
  ) {
    card.animationState = state;
  }

  onAnimationEnd(
    _: AnimationEvent,
    card: { image: string; alt: string; author: string; title: string; animationState: string },
  ) {
    if (card.animationState === 'shrink') {
      this.loadImage(this.pictures[Math.floor(Math.random() * this.pictures.length)], card);
      card.animationState = 'expand';
    }
  }

  fetchImages(): void {
    this.sharedMediaService.getPublicSharedMediaByTournament(this.tournamentId()!).subscribe({
      next: (data: SharedMediaMetadataDto[]) => {
        this.pictures = data;
      },
      error: error => {
        console.error('Error fetching pictures:', error);
      },
    });
  }

  checkUploadLinkString(): boolean {
    return (
      this.linkToUpload === '' || this.linkToUpload.endsWith(`${this.tournamentId()}/upload-image`)
    );
  }

  loadImage(
    imgEntry: SharedMediaMetadataDto,
    card: { image: string; alt: string; author: string; title: string; animationState: string },
  ): void {
    if (imgEntry?.id !== undefined) {
      this.sharedMediaService.getPublicSharedMediaImage(imgEntry.id).subscribe(
        response => {
          card.image = URL.createObjectURL(response as any); // eslint-disable-line
          card.alt = imgEntry.title ?? 'Tournament Picture';
          card.author = imgEntry.author ?? 'Mysterious KnowOne';
          card.title = imgEntry.title ?? 'Tournament Picture';
        },
        error => {
          // Handle errors
          console.error('Error fetching shared media image:', error);
          card.image = 'src';
          card.alt = 'Error fetching specific image';
        },
      );
    } else {
      console.warn('Card trying to display undefined resources');
      card.image = 'src';
      card.alt = 'undefined resource';
    }
  }
}
